import { useFormController } from '@dreryk/react-common';

import { AdditionalInformationTextarea } from '../../shared/AdditionalInformationTextarea';

import {
   ReferralToAnalyticalExaminationFormBean,
   ReferralToAnalyticalExaminationFormController,
} from './ReferralToAnalyticalExaminationForm.types';
import { StyledAnalyticalExaminationFormContent } from './StyledAnalyticalExaminationFormContent';
import { StyledAnalyticalExaminationFormFields } from './StyledAnalyticalExaminationFormFields';
import { StyledReferralToAnalyticalExaminationForm } from './StyledReferralToAnalyticalExaminationForm';
import { ANALYTICAL_EXAMINATION_TYPES, AnalyticalExaminationTable } from './fields/AnalyticalExaminationTable';
import { AnalyticalExaminationTypeAutosuggest } from './fields/AnalyticalExaminationTypeAutosuggest';
import { useAnalyticalExaminationTypeSelector } from './fields/AnalyticalExaminationTypeAutosuggest.hooks';
import { FrequentAnalyticalExaminationsTable } from './fields/FrequentAnalyticalExaminationsTable';

const getInitialValues = (
   referralToAnalyticalExaminationDraft: ReferralToAnalyticalExaminationFormBean | null
): ReferralToAnalyticalExaminationFormBean => ({
   additionalInformation: referralToAnalyticalExaminationDraft?.additionalInformation ?? null,
   analyticalExaminationTypes: referralToAnalyticalExaminationDraft?.analyticalExaminationTypes ?? [],
});

type ReferralToAnalyticalExaminationFormChildrenProps = Pick<
   ReferralToAnalyticalExaminationFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'reset'
>;

type ReferralToAnalyticalExaminationFormProps = {
   readonly referralToAnalyticalExaminationDraft: ReferralToAnalyticalExaminationFormBean | null;
   readonly children: (childrenProps: ReferralToAnalyticalExaminationFormChildrenProps) => JSX.Element;
};

const ReferralToAnalyticalExaminationForm = (props: ReferralToAnalyticalExaminationFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      handleSubmitDraft,
      reset,
      control,
      formState,
   } = useFormController<ReferralToAnalyticalExaminationFormBean>(
      getInitialValues(props.referralToAnalyticalExaminationDraft)
   );
   const selector = useAnalyticalExaminationTypeSelector(control, ANALYTICAL_EXAMINATION_TYPES);

   return (
      <StyledReferralToAnalyticalExaminationForm onSubmit={(event) => event.preventDefault()} noValidate>
         <StyledAnalyticalExaminationFormContent testId={'analyticalExamination'}>
            <StyledAnalyticalExaminationFormFields>
               <AnalyticalExaminationTypeAutosuggest
                  addAnalyticalExaminationType={selector.addItem}
                  invalid={selector.invalid}
                  validateSelector={selector.validate}
               />

               <FrequentAnalyticalExaminationsTable
                  selectFrequentAnalyticalExamination={selector.addItem}
                  selectedItems={selector.selectedItems}
               />
            </StyledAnalyticalExaminationFormFields>
            <StyledAnalyticalExaminationFormFields>
               <AnalyticalExaminationTable removeItem={selector.removeItem} data={selector.selectedItems} />
               <AdditionalInformationTextarea register={register} />
            </StyledAnalyticalExaminationFormFields>
            {props.children({
               handleSubmit: handleSubmit,
               handleSubmitDraft: handleSubmitDraft,
               reset: reset,
               formState: formState,
            })}
         </StyledAnalyticalExaminationFormContent>
      </StyledReferralToAnalyticalExaminationForm>
   );
};

export { ReferralToAnalyticalExaminationForm };
